<template>
  <div class="page-view">

    <BasePageHeader :title="category?.name || $t('entity.category')" @back="$router.back()">
      <template #actions>
        <button v-if="!editing" class="btn ghost sm" @click="startEdit">{{ $t('common.edit') }}</button>
        <template v-else>
          <button class="btn ghost sm" @click="cancelEdit">{{ $t('common.cancel') }}</button>
          <button class="btn primary sm" :disabled="saving || !form.name" @click="save">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </template>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="!category" variant="error" :message="$t('categories.notFound')" />

    <template v-else>

      <!-- Info card -->
      <div class="mx-4 my-4 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
        <template v-if="!editing">
          <div class="flex items-baseline gap-3 px-4 py-3 border-b border-[var(--color-border)]">
            <span class="w-24 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('common.name') }}</span>
            <span class="flex-1 text-sm text-[var(--color-text)]">{{ category.name }}</span>
          </div>
          <div class="flex items-baseline gap-3 px-4 py-3">
            <span class="w-24 shrink-0 text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide">{{ $t('categories.parent') }}</span>
            <span class="flex-1 text-sm text-[var(--color-text)]">
              <button v-if="parentCategory" class="text-[var(--color-primary)] hover:underline" @click="router.push(`/categories/${parentCategory.id}`)">
                {{ parentCategory.name }}
              </button>
              <span v-else class="text-[var(--color-muted)]">{{ $t('common.none') }}</span>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="px-4 py-2.5 border-b border-[var(--color-border)]">
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="form.name" placeholder="Kategori navn" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5">
            <BaseFormField :label="$t('categories.parentCategory')">
              <BaseSelect v-model="form.parent">
                <option :value="null">{{ $t('common.none') }}</option>
                <option v-for="c in otherRootCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </BaseSelect>
            </BaseFormField>
          </div>
        </template>
      </div>

      <!-- Subcategories -->
      <div v-if="subcategories.length" class="mx-4 mb-4">
        <div class="section-label mb-2">{{ $t('categories.subcategoriesSection') }}</div>
        <div class="flex flex-col">
          <div
            v-for="(sub, i) in subcategories"
            :key="sub.id"
            class="data-card cursor-pointer"
            :class="i < subcategories.length - 1 ? 'rounded-b-none border-b-0' : ''"
            @click="router.push(`/categories/${sub.id}`)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ sub.name }}</div>
              <div v-if="itemCountForCategory(sub.id)" class="data-card__meta">
                <span class="badge badge-accent">{{ $t('categories.viewAllItems', { count: itemCountForCategory(sub.id) }) }}</span>
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 mr-2"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </div>
      </div>

      <!-- Items preview -->
      <div class="mx-4 mb-4">
        <div class="flex items-center mb-2">
          <div class="section-label flex-1">{{ $t('categories.itemsSection') }}</div>
          <button
            v-if="itemsInCategory.length"
            class="btn ghost sm"
            @click="router.push({ path: '/items', query: { category: categoryId } })"
          >
            {{ $t('categories.viewAll', { count: itemsInCategory.length }) }}
          </button>
        </div>

        <EmptyState v-if="!itemsInCategory.length" :message="$t('categories.noItems')" />
        <div v-else class="flex flex-col">
          <div
            v-for="(item, i) in previewItems"
            :key="item.id"
            class="data-card cursor-pointer"
            :class="(i < previewItems.length - 1 || itemsInCategory.length > 5) ? 'rounded-b-none border-b-0' : ''"
            @click="router.push(`/item/${item.id}`)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ item.name }}</div>
              <div v-if="item.item_type" class="data-card__meta">
                <span class="badge badge-muted">{{ typeLabel[item.item_type] ?? $t('itemTypes.unknown') }}</span>
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-[var(--color-muted)] shrink-0 mr-2"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
          <div
            v-if="itemsInCategory.length > 5"
            class="data-card rounded-t-none cursor-pointer justify-center text-sm text-[var(--color-primary)] font-medium"
            @click="router.push({ path: '/items', query: { category: categoryId } })"
          >
            {{ $t('categories.viewAll', { count: itemsInCategory.length }) }}
          </div>
        </div>
      </div>

      <!-- Danger zone -->
      <div class="mx-4 mb-6">
        <div class="section-label danger mb-2">{{ $t('common.dangerZone') }}</div>
        <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl px-4 py-3 flex items-center justify-between">
          <span class="text-sm text-[var(--color-muted)]">{{ $t('categories.deleteHint') }}</span>
          <button class="btn danger sm" @click="showDeleteConfirm = true">{{ $t('common.delete') }}</button>
        </div>
      </div>

    </template>

    <BaseConfirmModal
      :show="showDeleteConfirm"
      :title="$t('categories.deleteConfirmTitle')"
      :body="$t('categories.deleteConfirmBodySimple', { name: category?.name })"
      :confirm-label="$t('common.delete')"
      :loading="deleting"
      @confirm="doDelete"
      @cancel="showDeleteConfirm = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import { updateCategory, deleteCategory } from '@/api/api'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'

const store = useStore()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const { t } = useI18n()

const loading = ref(false)
const editing = ref(false)
const saving = ref(false)
const showDeleteConfirm = ref(false)
const deleting = ref(false)
const form = ref({ name: '', parent: null })

const typeLabel = computed(() => ({
  1: t('itemTypes.1'),
  2: t('itemTypes.2'),
  3: t('itemTypes.3'),
  4: t('itemTypes.4'),
}))

const categoryId = computed(() => route.params.categoryId)

const category = computed(() =>
  store.categories.find(c => String(c.id) === String(categoryId.value)) ?? null
)

const parentCategory = computed(() =>
  category.value?.parent
    ? store.categories.find(c => c.id === category.value.parent) ?? null
    : null
)

const subcategories = computed(() =>
  store.categories.filter(c => String(c.parent) === String(categoryId.value))
)

const otherRootCategories = computed(() =>
  store.categories.filter(c => c.parent === null && String(c.id) !== String(categoryId.value))
)

const itemsInCategory = computed(() =>
  store.items.filter(m => {
    const id = typeof m.category === 'object' ? m.category?.id : m.category
    return String(id) === String(categoryId.value)
  })
)

const previewItems = computed(() => itemsInCategory.value.slice(0, 5))

function itemCountForCategory(catId) {
  return store.items.filter(m => {
    const id = typeof m.category === 'object' ? m.category?.id : m.category
    return String(id) === String(catId)
  }).length
}

onMounted(async () => {
  loading.value = true
  try {
    if (!store.categories.length) await store.fetchCategories()
    if (!store.items.length && !store.isLoadingItems) store.fetchItems()
  } finally {
    loading.value = false
  }
})

function startEdit() {
  form.value = { name: category.value.name, parent: category.value.parent ?? null }
  editing.value = true
}

function cancelEdit() {
  editing.value = false
}

async function save() {
  saving.value = true
  try {
    await updateCategory(categoryId.value, form.value)
    toast.success(t('categories.updated'))
    await store.fetchCategories(true)
    editing.value = false
  } catch {
    toast.error(t('categories.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function doDelete() {
  deleting.value = true
  try {
    await deleteCategory(categoryId.value)
    toast.success(t('categories.deleted'))
    await store.fetchCategories(true)
    router.push('/categories')
  } catch {
    toast.error(t('categories.deleteFailed'))
  } finally {
    deleting.value = false
    showDeleteConfirm.value = false
  }
}
</script>
